import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { Button } from "./button";
import { Input } from "./input";
import { Checkbox } from './checkbox';
import { Label } from "./label"
import { useToast } from "./use-toast";

import { useNavigate, useLocation } from 'react-router-dom';
import MapComponent from './MapComponent';
import ActivityFilterPanel from './ActivityFilterPanel';
import StravaComponent from './stravacomponent';
import GarminComponent from './garmincomponent';
import IntegratedFrameSettings from './IntegratedFrameSettings';
import GPXUploadHandler from './gpx-handler';
import { ImageSlideshow } from './imageslideshow';
import StaticImageSlideshow from './staticimageslideshow';
import { SharedStylesGrid } from './sharedstyles';
import FormatSelectorPopup from './formatselector';
import ColorSettingsPopup from './ColorSettingsPopup';
import ColorLegend from './ColorLegend';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { AlertTriangle, CheckCircle2, XCircle, X, Maximize2, Loader2, Upload, MapPin, Hand, Plus, Minus, Scaling, Palette, Map, Frame, History, Activity, Image as ImageIcon, Check, Unlock, ArrowLeft, ArrowRight, Mountain, Touchpad, SlidersHorizontal } from "lucide-react"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { motion, AnimatePresence } from "framer-motion"
import MapPurchaseSection from './mappurchuase';
import AdminPanel from './adminpanel';
import { MobileMapLayout } from './mobilepurchase';

import StravaPoweredLogo2 from './img/api_logo_pwrdBy_strava_stack_light.svg'
import TwoFingerZoomImg from './img/twofingerzoom.png'
import GarminLogo from './img/garmin_logo.png'
import borderExample from './img/border_example.png';

import { PRICES } from '../config/prices';

import { CSS4_COLORS } from './mplcolors';
import { FORMATS } from '../config/prices';
import { Slider } from './slider';
import { trackActivityEvents, trackGenerationEvents, trackPurchaseEvents, trackMapConfigEvents } from '../utils/ga4Events';

const PREVIEW_LOCATIONS = ["Bescano, Spain", "Girona, Spain"];

const DEFAULT_MAP_SETTINGS = {
  km2: 500,
  figure_width: 80,
  figure_height: 60,
  // Intentionally empty: a stale persisted name doesn't match the current
  // map state after manual drags, so we always start blank and let the user
  // fill it via search. Last-generated map keeps its own copy in
  // recent_images.place_name for the print label.
  placeName: "",
  activities: { color: 'sienna', alpha: 0.5, active: true, lineWidth: 0.8, byTypeEnabled: false, byType: {} },
  buildings: { color: 'darkgrey', alpha: 0.8, active: true },
  forests: { color: 'forestgreen', alpha: 0.1, active: true },
  naturalareas: { color: 'darkgreen', alpha: 0.2, active: true },
  water: { color: 'cornflowerblue', alpha: 0.4, active: true },
  oceans: { color: 'royalblue', alpha: 0.4, active: true },
  streets: { color: 'black', alpha: 0.2, active: true, details: 'roads' },
  land: { color: 'palegoldenrod', alpha: 0.15, active: true }
};

const DEFAULT_FRAME_SETTINGS = {
  addText: false,
  textPosition: 'bottom',
  frameColor: '#ffffff',
  frameSize: 2,
  fontColorTop: '#000000',
  fontColorBottom: '#808080',
  coordinateFormat: 'decimal',
  framePercentage: 10,
  frameWidth: 0,
  line1Items: [],
  line2Items: [],
  availableItems: [
    { id: 'coordinates_dms', content: 'Coordinates (DMS)' },
    { id: 'coordinates_metric', content: 'Coordinates (Metric)' },
    { id: 'place_name', content: 'Place Name' },
    { id: 'custom_text', content: 'Custom Text' },
    { id: 'distance', content: 'Distance' },
    { id: 'hours', content: 'Hours' },
    { id: 'elevation', content: 'Elevation' }
  ]
};

const DEFAULT_FORMAT = {
  value: '4:3',
  size: 'S',
  label: '4:3 S',
  dimensions: { width: 28, height: 21 },
  aspectRatio: 1.333
};

// Master switch for the (currently broken) custom-frame feature. When false:
//   - the "Frame" tab is hidden from the section nav
//   - the generate payload always sends frame=false / no frameSettings
//   - any saved addFrame=true is ignored on load
// Flip to true to re-enable once the frame compositing is fixed.
const FRAME_ENABLED = false;

// Progress bar + label fed by the polled `generationStatus`. Used in the
// preview section to render server-side render progress; survives reloads
// because the underlying state lives in the user record on the backend.
const GenerationProgress = ({ status }) => {
  if (!status) return null;
  if (status.state === 'idle' || status.state === 'done') return null;
  const isError = status.state === 'error';
  const pct = Math.max(0, Math.min(100, status.progress || 0));
  return (
    <div className="w-full max-w-xs mx-auto flex flex-col items-center space-y-2">
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${isError ? 'bg-red-500' : 'bg-gradient-to-r from-black/90 via-black to-black/70'}`}
          style={{ width: `${isError ? 100 : pct}%` }}
        />
      </div>
      <div className="text-xs text-gray-700 text-center">
        {isError
          ? (status.error || 'Generation failed')
          : `${status.label || 'Working…'} (${pct}%)`}
      </div>
    </div>
  );
};

// The native date picker emits a Date at local-midnight. When we ship that
// to the backend filter as the *upper* bound, an activity recorded later
// today (e.g. 10:00) is excluded because midnight precedes 10:00. Bump to
// end-of-day for `to` and start-of-day for `from` before serializing so
// the user's "today" picks include the entire day.
const dateBoundaryISO = (d, kind) => {
  if (!d) return null;
  const out = new Date(d);
  if (kind === 'end') {
    out.setHours(23, 59, 59, 999);
  } else {
    out.setHours(0, 0, 0, 0);
  }
  return out.toISOString();
};

// Two rectangles are "similar enough" to reuse a cached max_dpis table when
// their km² match within `areaTolerance` and their centers sit within
// `distanceKm`. The DPI table mostly tracks (a) the QGIS pixel-size cliff
// (function of size+dpi only — same everywhere) and (b) global topography-
// data availability, which doesn't change appreciably across moderate
// distances. So a translated rectangle of similar size yields the same caps.
const rectangleBoundsAreSimilar = (
  prevBounds,
  currBounds,
  prevKm2,
  currKm2,
  { distanceKm = 200, areaTolerance = 0.1 } = {},
) => {
  if (!prevBounds || !currBounds) return false;
  if (prevBounds.length !== 2 || currBounds.length !== 2) return false;
  if (prevKm2 == null || currKm2 == null || prevKm2 <= 0) return false;
  if (Math.abs(prevKm2 - currKm2) / prevKm2 > areaTolerance) return false;

  const center = (b) => [(b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2];
  const [aLat, aLon] = center(prevBounds);
  const [bLat, bLon] = center(currBounds);
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371; // earth radius in km
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  const distance = 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return distance <= distanceKm;
};

export default function Dashboard({ userData, onLogout, selectedTab, setSelectedTab, onAdminStatus }) {
  const [user, setUser] = useState(null);
  const [file, setFile] = useState(null);
  const [genrationError, setGenerationError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stravaStatus, setStravaStatus] = useState({
    isConnected: false,
    numActivities: 0,
    activities: null,
    error: null,
    isLoading: false,
    lastSync: null,
  });
  const [garminStatus, setGarminStatus] = useState({
    connected: false,
    isLoading: false,
    activities: null,
    lastSync: null,
    error: null,
    activityCount: 0,
    currentProgress: 0,
    expectedCount: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  // Snapshot of the inputs at the last successful generation. Persisted to
  // localStorage so the "no changed settings" guard on the Generate button
  // survives page reloads — without this, a fresh mount would treat every
  // current settings value as "different from null" and re-enable the button.
  const [lastGeneratedSettings, setLastGeneratedSettings] = useState(() => {
    try {
      const raw = localStorage.getItem('lastGeneratedSettings');
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (lastGeneratedSettings) {
        localStorage.setItem('lastGeneratedSettings', JSON.stringify(lastGeneratedSettings));
      } else {
        localStorage.removeItem('lastGeneratedSettings');
      }
    } catch (_e) { /* quota / private mode — no-op */ }
  }, [lastGeneratedSettings]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_MAP_SETTINGS);
  const [selectedFormat, setSelectedFormat] = useState(DEFAULT_FORMAT);
  const [isPortrait, setIsPortrait] = useState(false);
  const [mapCenter, setMapCenter] = useState([42.0, 2.83]);
  const [rectangleBounds, setRectangleBounds] = useState([[41.95, 2.78], [42.05, 2.88]]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [allDates, setAllDates] = useState(true);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d;
  });
  const [toDate, setToDate] = useState(new Date());
  const topography = true; // Always on: backend map generation requires topography in every print.
  const [mapStyle, setMapStyle] = useState('satellite'); // preview tile layer: 'standard' | 'topo' | 'satellite'
  const [addFrame, setAddFrame] = useState(false);
  const [frameSettings, setFrameSettings] = useState(DEFAULT_FRAME_SETTINGS);
  const [activitySource, setActivitySource] = useState('none');
  const [activitiesInView, setActivitiesInView] = useState([]);
  const [hiddenActivityIds, setHiddenActivityIds] = useState([]);
  const [syncWaterOceans, setSyncWaterOceans] = useState(false);

  const [commutes, setCommutes] = useState(false);

  const [enabledActivities, setEnabledActivities] = useState({});
  const [garminActivities, setGarminActivities] = useState({});
  const [totalEligibleActivities, setTotalEligibleActivities] = useState(0);
  const [previewData, setPreviewData] = useState(
    Object.fromEntries(PREVIEW_LOCATIONS.map(location => [location, { status: 'empty' }]))
  );
  const [isGeneratingPreviews, setIsGeneratingPreviews] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    showCommute: false,
  });
  const [manualActivities, setManualActivities] = useState([]);
  const [isLoadingManual, setIsLoadingManual] = useState(false);
  const [isLoadingFrame, setIsLoadingFrame] = useState(false);

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [finalImage, setFinalImage] = useState(null);
  const [frameImage, setFrameImage] = useState(null);
  const [isGeneratingFinal, setIsGeneratingFinal] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  // Persistent server-side generation state — polled via
  // /user/generation-status. Survives reloads and dedupes across tabs.
  // Shape: { state: 'idle'|'running'|'done'|'error', label, progress, image_id?, error? }
  const [generationStatus, setGenerationStatus] = useState(null);
  const [isFormatSelectorOpen, setIsFormatSelectorOpen] = useState(false);
  const [voucher, setVoucher] = useState('');

  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState({});

  const [activeMapSection, setActiveMapSection] = React.useState('map');
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // 'classic' shows the bundled StaticImageSlideshow; 'shared' swaps in the
  // user-shared styles grid. The toggle lives in the same row as Customize.
  const [styleSource, setStyleSource] = useState('classic');
  const [expandedCategory, setExpandedCategory] = useState(null);
  // Which side-panel is currently shown next to the map. Legacy: this
  // controlled Location / Format / Size / Activities tabs. After the
  // restructure, only the Activities panel is shown here, and the user can
  // collapse it so the map uses the full width.
  const [rightPanel, setRightPanel] = useState(() => {
    try {
      return localStorage.getItem('rightPanel') || 'location';
    } catch {
      return 'format';
    }
  });
  const [activitiesPanelOpen, setActivitiesPanelOpen] = useState(() => {
    try {
      return localStorage.getItem('activitiesPanelOpen') === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('activitiesPanelOpen', String(activitiesPanelOpen)); } catch (_e) { }
  }, [activitiesPanelOpen]);
  // Recent place searches are server-owned; we hydrate from backend on mount
  // and round-trip each new selection. localStorage is used only as an
  // optimistic cache so the chips show up before the fetch completes.
  const [recentPlaces, setRecentPlaces] = useState(() => {
    try {
      const stored = localStorage.getItem('recentPlaces');
      return stored ? JSON.parse(stored).slice(0, 5) : [];
    } catch (_e) {
      return [];
    }
  });
  const [customRatioW, setCustomRatioW] = useState('');
  const [customRatioH, setCustomRatioH] = useState('');

  const cacheRecentPlaces = (places) => {
    try { localStorage.setItem('recentPlaces', JSON.stringify(places)); } catch (_e) { }
  };

  const rememberRecentPlace = async (name) => {
    if (!name) return;
    setRecentPlaces(prev => {
      const next = [name, ...prev.filter(p => p !== name)].slice(0, 5);
      cacheRecentPlaces(next);
      return next;
    });
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const url = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
      const response = await fetch(`${url}/user/recent-places`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ place: name }),
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.places)) {
          setRecentPlaces(data.places);
          cacheRecentPlaces(data.places);
        }
      }
    } catch (_e) {
      // Non-fatal: the optimistic cache keeps the UI functional.
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const url = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
    fetch(`${url}/user/recent-places`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data && Array.isArray(data.places)) {
          setRecentPlaces(data.places);
          cacheRecentPlaces(data.places);
        }
      })
      .catch(() => { });
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  const mapSections = [
    { id: 'map', label: 'Map', icon: <Map className="h-6 w-6" /> },
    { id: 'style', label: 'Style', icon: <Palette className="h-6 w-6" /> },
    ...(FRAME_ENABLED ? [{ id: 'frame', label: 'Frame', icon: <Frame className="h-6 w-6" /> }] : []),
    { id: 'preview', label: 'Preview', icon: <ImageIcon className="h-6 w-6" /> }
  ];
  const isValidMapSection = (id) => mapSections.some((s) => s.id === id);
  const [shouldCenter, setShouldCenter] = useState(false);
  const [shouldZoom, setShouldZoom] = useState(false);
  const [zoom, setZoom] = useState(10);
  // Bumping this nonce tells MapComponent to imperatively setView to the
  // current mapCenter + zoom. Used after loadSettings to restore the saved
  // position (MapContainer otherwise latches its initial center/zoom on
  // mount and ignores later prop changes).
  const [restoreViewNonce, setRestoreViewNonce] = useState(0);
  const initStravaChecks = useRef(false);
  // Blocks the localStorage save effect until loadSettings has completed —
  // without this gate the effect fires with defaults on first paint and
  // overwrites the saved snapshot before we've had a chance to read it.
  const settingsLoadedRef = useRef(false);
  // Mirror of settingsLoadedRef as reactive state. Used to delay mounting
  // the leaflet map until restored `mapCenter` / `zoom` / `rectangleBounds`
  // are in state — MapContainer only reads center/zoom at mount, so
  // mounting with defaults then updating props doesn't actually move the
  // map view. Gating here avoids the Girona flash-and-stick on reload.
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [maxDpis, setMaxDpis] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

  const handleMapCenterChange = (newCenter) => {
    setMapCenter(newCenter);
  };

  // useEffect(() => {
  //   const params = new URLSearchParams(window.location.search);
  //   const tab = params.get('tab');
  //   if (tab) {
  //     setSelectedTab(tab);
  //   }
  // }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const sectionParam = params.get('section');
    const storedSection = localStorage.getItem('activeMapSection');

    if (tabParam) {
      setSelectedTab(tabParam);
    }

    // Guard against legacy section ids (e.g. `place`, `activities`) left in
    // URLs or localStorage from before the section reorder — unknown ids
    // would render nothing and the dashboard would look "empty".
    if (sectionParam && isValidMapSection(sectionParam)) {
      setActiveMapSection(sectionParam);
    } else if (storedSection && isValidMapSection(storedSection)) {
      setActiveMapSection(storedSection);
      // Only update URL if there's no section param
      if (!sectionParam) {
        navigate(`${location.pathname}?section=${storedSection}`, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('activeMapSection', activeMapSection);
  }, [activeMapSection]);

  useEffect(() => {
    localStorage.setItem('rightPanel', rightPanel);
  }, [rightPanel]);

  const handleSectionChange = (newSection) => {
    setActiveMapSection(newSection);
    localStorage.setItem('activeMapSection', newSection);

    const params = new URLSearchParams(location.search);
    params.set('section', newSection);

    // Preserve existing tab parameter if it exists
    const tabParam = params.get('tab');
    if (tabParam) {
      params.set('tab', tabParam);
    }

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleTabChange = (newTab) => {
    setSelectedTab(newTab);

    const params = new URLSearchParams(location.search);
    params.set('tab', newTab);

    // Preserve section parameter
    params.set('section', activeMapSection);

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const applySettings = (data) => {
    if (!data) {
      return;
    }

    // Set all individual states with null checks
    if (data.settings && typeof data.settings === 'object' && Object.keys(data.settings).length > 0) {
      // Merge over defaults so new fields (e.g. byTypeEnabled) don't disappear
      // if the saved snapshot predates them. placeName is force-cleared
      // because the saved value may not match the current map view (manual
      // drags don't update it), so we'd rather show a blank pill than
      // mislead the user with a stale label.
      setSettings({ ...DEFAULT_MAP_SETTINGS, ...data.settings, placeName: '' });
    }
    if (data.selectedFormat) setSelectedFormat(data.selectedFormat || DEFAULT_FORMAT);
    if (data.isPortrait !== undefined) setIsPortrait(data.isPortrait);
    if (data.mapCenter) setMapCenter(data.mapCenter);
    if (typeof data.zoom === 'number') setZoom(data.zoom);
    if (data.rectangleBounds) setRectangleBounds(data.rectangleBounds);
    if (data.activitySource) setActivitySource(data.activitySource);
    if (data.filteredActivities) setFilteredActivities(data.filteredActivities);
    if (data.enabledActivities) setEnabledActivities(data.enabledActivities);
    if (data.garminActivities) setGarminActivities(data.garminActivities);
    if (data.allDates !== undefined) setAllDates(data.allDates);
    if (data.dateFrom) setFromDate(new Date(data.dateFrom));
    if (data.dateTo) setToDate(new Date(data.dateTo));
    // topography is always on now; ignore any saved preference.
    // Force-off if the feature is disabled — even if a saved snapshot has
    // addFrame=true, we don't want it leaking into the generate payload.
    if (data.frame !== undefined) setAddFrame(FRAME_ENABLED ? data.frame : false);
    if (data.syncWaterOceans) setSyncWaterOceans(data.syncWaterOceans);
    if (data.frameSettings) setFrameSettings(data.frameSettings || DEFAULT_FRAME_SETTINGS);
    if (Array.isArray(data.hiddenActivityIds)) setHiddenActivityIds(data.hiddenActivityIds);
    if (typeof data.commutes === 'boolean') setCommutes(data.commutes);
    // URL `?section=` wins over the saved snapshot, so only apply these when
    // the URL hasn't already supplied a value.
    const urlParams = new URLSearchParams(window.location.search);
    if (data.activeMapSection && isValidMapSection(data.activeMapSection) && !urlParams.get('section')) {
      setActiveMapSection(data.activeMapSection);
    }
    if (data.rightPanel) setRightPanel(data.rightPanel);

    // if (data.settings.km2 === 0) {
    //   setTopography(false);
    // }
  };

  useEffect(() => {
    const loadSettings = async () => {
      // localStorage is the primary source — written synchronously on every
      // state change (see the save effect below). Backend is the fallback
      // for fresh devices / cleared storage.
      try {
        const localData = localStorage.getItem('dashboardSettings');
        if (localData) {
          const parsed = JSON.parse(localData);
          if (parsed?.frameSettings) parsed.frameSettings.addText = false;
          applySettings(parsed);
          if (parsed.rectangleBounds && parsed.rectangleBounds.length === 2) {
            const bounds = parsed.rectangleBounds;
            const centerLat = (bounds[0][0] + bounds[1][0]) / 2;
            const centerLon = (bounds[0][1] + bounds[1][1]) / 2;
            setMapCenter([centerLat, centerLon]);
          }
          // Bump the view-restore nonce so MapComponent imperatively moves
          // the leaflet map to the restored center + zoom. MapContainer
          // only honors its center/zoom props at initial mount, so without
          // this nudge the view stays at the Girona default.
          setRestoreViewNonce((n) => n + 1);
          return;
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "An error occured during loading settings",
          className: "bg-red-300 text-black",
          duration: 3000,
        });
      }

      try {
        const response = await fetch(`${API_URL}/user/settings`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const backendData = await response.json();
          if (backendData?.frameSettings) backendData.frameSettings.addText = false;
          applySettings(backendData);
          if (backendData.rectangleBounds && backendData.rectangleBounds.length === 2) {
            const bounds = backendData.rectangleBounds;
            const centerLat = (bounds[0][0] + bounds[1][0]) / 2;
            const centerLon = (bounds[0][1] + bounds[1][1]) / 2;
            setMapCenter([centerLat, centerLon]);
          }
          setRestoreViewNonce((n) => n + 1);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "An error occured during loading settings",
          className: "bg-red-300 text-black",
          duration: 3000,
        });
      }
    };

    loadSettings().finally(() => {
      // Release the save gate once we've either applied saved data or
      // confirmed there was none. setState calls in applySettings are batched
      // and flush before this runs, so the save effect will fire with the
      // restored values rather than the initial defaults.
      settingsLoadedRef.current = true;
      setSettingsLoaded(true);
    });
  }, []);

  useEffect(() => {
    // Fetch user status when component mounts

    const checkAdminStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/user/status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        // Log the raw response for debugging
        const rawResponse = await response.text();
        // console.log('Raw response:', rawResponse);

        // Try to parse if it looks like JSON
        try {
          const data = JSON.parse(rawResponse);
          const admin = data.is_admin || false;
          setIsAdmin(admin);
          if (onAdminStatus) onAdminStatus(admin);
        } catch (parseError) {
          setIsAdmin(false);
          if (onAdminStatus) onAdminStatus(false);
        }

      } catch (error) {
        setIsAdmin(false);
        if (onAdminStatus) onAdminStatus(false);
      }
    };

    checkAdminStatus();
  }, []);

  // Persist the full dashboard snapshot to localStorage on every change.
  // The gate ensures this doesn't fire with defaults before loadSettings has
  // had a chance to restore the last session.
  useEffect(() => {
    if (!settingsLoadedRef.current) return;
    const currentSettings = {
      settings,
      selectedFormat,
      isPortrait,
      mapCenter,
      zoom,
      rectangleBounds,
      activitySource,
      filteredActivities,
      enabledActivities,
      garminActivities,
      allDates,
      dateFrom: dateBoundaryISO(fromDate, 'start'),
      dateTo: dateBoundaryISO(toDate, 'end'),
      topography,
      frame: FRAME_ENABLED ? addFrame : false,
      frameSettings,
      syncWaterOceans,
      activeMapSection,
      rightPanel,
      hiddenActivityIds,
      commutes,
    };
    try {
      localStorage.setItem('dashboardSettings', JSON.stringify(currentSettings));
    } catch {
      // Quota exceeded or storage disabled — fall through, backend save
      // still runs on Generate.
    }
  }, [
    settings,
    selectedFormat,
    isPortrait,
    mapCenter,
    zoom,
    rectangleBounds,
    filteredActivities,
    enabledActivities,
    garminActivities,
    allDates,
    fromDate,
    toDate,
    topography,
    addFrame,
    frameSettings,
    activitySource,
    syncWaterOceans,
    activeMapSection,
    rightPanel,
    hiddenActivityIds,
    commutes,
  ]);

  const saveToBackend = async (finalDimensions) => {
    const settingsToSave = {
      settings: {
        ...settings,
        ...finalDimensions
      },
      selectedFormat,
      isPortrait,
      mapCenter,
      rectangleBounds,
      filteredActivities,
      enabledActivities,
      garminActivities,
      allDates,
      dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
      dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
      topography,
      frame: FRAME_ENABLED ? addFrame : false,
      frameSettings,
      activitySource,
    };

    await fetch(`${API_URL}/user/settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settingsToSave)
    });
  };

  // const [stravaStatus, setStravaStatus] = useState({
  //   isConnected: false,
  //   numActivities: 0,
  //   activities: null,
  //   error: null,
  //   isLoading: false,
  //   lastSync: null,
  // });

  useEffect(() => {
    let pollingInterval;
    let attemptCount = 0;
    const MAX_ATTEMPTS = 2;

    const checkStravaData = async () => {
      try {
        const response = await fetch(`${API_URL}/user/strava-data-status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // If we get a 405, stop polling and set not connected
        if (response.status === 405) {
          setStravaStatus(prev => ({
            ...prev,
            isConnected: false,
            isLoading: false,
            lastSync: null,
            activities: null,
            numActivities: 0,
          }));
          return;
        }

        const data = await response.json();

        setStravaStatus(prev => ({
          isConnected: data.isConnected,
          isLoading: data.isLoading,
          activities: data.activities,
          numActivities: data.numActivities,
          lastSync: data.lastSync || (data.activities ? new Date().toISOString() : null),
        }));

      } catch (error) {
        setStravaStatus(prev => ({
          ...prev,
          error: "Error fetching Strava data",
          isLoading: false,
        }));
      }
    };

    // Initial userData checks
    if (userData) {
      initStravaChecks.current = true;
      // Do two checks with small delay
      checkStravaData();
      setTimeout(checkStravaData, 1000);
      return;
    }
  }, []);

  const checkStravaStatus = () => {
    setStravaStatus(prev => ({
      ...prev, isLoading: true,
    }));
  };

  const handleSyncWaterOceans = (checked) => {
    setSyncWaterOceans(checked);
  };

  const handleActivitySourceChange = (source) => {
    setActivitySource(source);
    if (source === 'none') {
      settings.activities.active = false;
    } else {
      settings.activities.active = true;
    }
    // if (source === 'manual') {
    //   fetchManualActivities();
    // }
  };

  const fetchManualActivities = async () => {
    if (!userData) return;

    setIsLoadingManual(true);
    try {
      const response = await fetch(`${API_URL}/user/manual-activities`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setManualActivities(Array.isArray(data.activities) ? data.activities : []);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured loading your GPX Files",
        className: "bg-red-300 text-black",
        duration: 3000,
      });
      setManualActivities([]);
    } finally {
      setIsLoadingManual(false);
    }
  };

  useEffect(() => {
    if (activitySource === 'manual') {
      fetchManualActivities();
    }
  }, [activitySource]);

  const toggleFrame = () => {
    setAddFrame(!addFrame);
  };

  const handleFormatSelect = (format) => {
    // Store original dimensions from the incoming format
    // console.log(format)

    // Calculate dimensions based on portrait mode
    const currentDimensions = isPortrait
      ? {
        figure_width: format.dimensions.height,
        figure_height: format.dimensions.width
      }
      : {
        figure_width: format.dimensions.width,
        figure_height: format.dimensions.height
      };

    // Update settings with the correct dimensions
    setSettings(prev => ({
      ...prev,
      ...currentDimensions
    }));

    // Calculate aspect ratio based on portrait mode
    const aspectRatio = isPortrait
      ? format.dimensions.height / format.dimensions.width
      : format.dimensions.width / format.dimensions.height;

    // Create the complete format object
    setSelectedFormat({
      ...format,
      aspectRatio,
      currentDimensions
    });
  };

  const togglePortrait = () => {
    if (!selectedFormat?.dimensions) return;

    const newIsPortrait = !isPortrait;
    setIsPortrait(newIsPortrait);

    // Get the original dimensions
    const { width, height } = selectedFormat.dimensions;

    // Create new dimensions based on portrait mode
    const newDimensions = newIsPortrait
      ? {
        figure_width: height,
        figure_height: width
      }
      : {
        figure_width: width,
        figure_height: height
      };

    // Update settings with new dimensions
    setSettings(prev => ({
      ...prev,
      ...newDimensions
    }));

    // Update selected format with new aspect ratio and dimensions
    setSelectedFormat(prev => ({
      ...prev,
      aspectRatio: newIsPortrait ? height / width : width / height,
      currentDimensions: newDimensions
    }));
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    setFilters(prev => ({ ...prev, startDate: date }));
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    setFilters(prev => ({ ...prev, enddate: date }));
  };

  const handleAllDatesChange = (checked) => {
    setAllDates(checked);
    // if (checked) {
    //   setFilters(prev => ({ ...prev, startDate: null, endDate: null }));
    // }
  };

  const handleCommuteChanged = (checked) => {
    setCommutes(checked);
    // setFilters(prev => ({ ...prev, showCommute: checked }));
    // updateFilteredActivities();
  };

  const handleActivitySelectionChange = (selectedActivities) => {
    if (activitySource === 'strava') {
      // Handle Strava activities
      if (stravaStatus.activities) {
        const newEnabledActivities = Object.keys(stravaStatus.activities).reduce((acc, activity) => {
          acc[activity] = selectedActivities.includes(activity);
          return acc;
        }, {});
        // setEnabledActivities(newEnabledActivities);
        setFilteredActivities(newEnabledActivities);
      }
    } else if (activitySource === 'garmin') {
      // Handle Garmin activities
      if (garminStatus.activities) {
        const newEnabledActivities = Object.keys(garminStatus.activities).reduce((acc, activity) => {
          acc[activity] = selectedActivities.includes(activity);
          return acc;
        }, {});
        setGarminActivities(newEnabledActivities);
      }
    } else if (activitySource === 'manual') {
      // Handle manual activities
      // For manual activities, selectedActivities is directly the new state
      setEnabledActivities(selectedActivities);
    }
    // updateFilteredActivities();
  };

  // useEffect(() => {
  //   if (activitySource === 'strava' && stravaStatus.activities) {
  //     const initialEnabledActivities = Object.keys(stravaStatus.activities).reduce((acc, type) => {
  //       acc[type] = true;
  //       return acc;
  //     }, {});
  //     setEnabledActivities(initialEnabledActivities);
  //   } else if (activitySource === 'manual' && manualActivities.length > 0) {
  //     console.log("INIT");
  //     const initialEnabledActivities = manualActivities.reduce((acc, activity) => {
  //       acc[activity._id] = true;
  //       return acc;
  //     }, {});
  //     setEnabledActivities(initialEnabledActivities);
  //   }
  // }, [stravaStatus.activities, manualActivities, activitySource]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const parseDimensions = (dimensionStr) => {
    const [width, height] = dimensionStr.split('x').map(d => parseFloat(d.replace(',', '.')));
    return { width: Number(width), height: Number(height) };
  };

  const handleSettingsChange = (setting, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [setting]: value,
    }));
  };


  const handleFrameSettingsChange = (newSettings) => {
    setFrameSettings(newSettings);
  };


  useEffect(() => {
    if (activeMapSection !== 'frame' || !addFrame) return;

    if (window.frameTimeout) {
      clearTimeout(window.frameTimeout);
    }

    setIsLoadingFrame(true);

    window.frameTimeout = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/generate_frame_preview`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(frameSettings),
        });
        const data = await response.json();
        setFrameImage(`data:image/png;base64,${data.image}`);
      } catch (error) {
        toast({
          title: "Error",
          description: "An error occured generating the frame preview",
          className: "bg-red-300 text-black",
          duration: 3000,
        });
      } finally {
        setIsLoadingFrame(false);
      }
    }, 1000);

    return () => {
      if (window.frameTimeout) {
        clearTimeout(window.frameTimeout);
      }
    };
  }, [addFrame, frameSettings]);

  const handleColorSettingChange = (category, setting, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [category]: {
        ...prevSettings[category],
        [setting]: value,
      },
    }));
  };

  const handleActivityTypeSettingChange = (activityType, setting, value) => {
    setSettings(prevSettings => {
      const prevByType = prevSettings.activities?.byType || {};
      // `__reset__` clears this type's override entirely so it falls back to
      // the common activity color/alpha/lineWidth.
      if (setting === '__reset__') {
        const { [activityType]: _removed, ...rest } = prevByType;
        return {
          ...prevSettings,
          activities: { ...prevSettings.activities, byType: rest },
        };
      }
      return {
        ...prevSettings,
        activities: {
          ...prevSettings.activities,
          byType: {
            ...prevByType,
            [activityType]: {
              ...(prevByType[activityType] || {}),
              [setting]: value,
            },
          },
        },
      };
    });
  };

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  const handlekm2change = async (e) => {
    const km2 = e.target.value === '' ? '' : Number(e.target.value);

    handleSettingsChange('km2', km2);

    if (window.km2timeout) {
      clearTimeout(window.km2timeout);
    }

    window.km2timeout = setTimeout(() => {
      const finalKm2 = Number(km2) || 1;
      handleSettingsChange('km2', finalKm2);

      if (finalKm2 > 4000 && finalKm2 <= 15000) {
        handleSettingsChange('buildings', { ...settings.buildings, active: false });
        handleSettingsChange('streets', { ...settings.streets, details: 'roads' });
      }
      if (finalKm2 > 15000) {
        handleSettingsChange('buildings', { ...settings.buildings, active: false });
        handleSettingsChange('streets', { ...settings.streets, active: false });
      }
      // if (finalKm2 > 20000) {
      //   handleSettingsChange('forests', { ...settings.forests, active: false });
      //   handleSettingsChange('naturalareas', { ...settings.naturalareas, active: false });
      // }
      // if (finalKm2 > 50000) {
      //   handleSettingsChange('water', { ...settings.water, active: false });
      // } else if (finalKm2 < 1) {
      //   setTopography(false)
      // }
    }, 1500);
    // Intentionally no setShouldZoom: km² changes resize the print-area
    // rectangle but must not alter the map's zoom. The only things that
    // should change zoom are the user (wheel/pinch) or the explicit
    // zoom-to-rectangle button.
  };

  useEffect(() => {
    // if (settings.km2 > 4000) {
    //   handleSettingsChange('buildings', { ...settings.buildings, active: false });
    //   handleSettingsChange('streets', { ...settings.streets, details: 'roads' });
    // }
    // else if (settings.km2 > 8000) {
    //   handleSettingsChange('buildings', { ...settings.buildings, active: false });
    //   handleSettingsChange('streets', { ...settings.streets, active: false });
    // }
    if (settings.km2 > 4000 && settings.km2 <= 8000) {
      // handleSettingsChange('buildings', { ...settings.buildings, active: false });
      handleSettingsChange('streets', { ...settings.streets, details: 'roads' });
    } else if (settings.km2 > 8000) {
      handleSettingsChange('buildings', { ...settings.buildings, active: false });
      handleSettingsChange('streets', { ...settings.streets, active: false });
    }
    // if (settings.km2 > 20000) {
    //   handleSettingsChange('forests', { ...settings.forests, active: false });
    //   handleSettingsChange('naturalareas', { ...settings.naturalareas, active: false });
    // }
    // if (settings.km2 > 50000) {
    //   handleSettingsChange('water', { ...settings.water, active: false });
    // } else if (settings.km2 < 1) {
    //   setTopography(false)
    // }
  }, [settings.km2]);
  // }, [settings.buildings.active]);

  const handlePlaceNameChange = async (e) => {
    const newPlaceName = e.target.value;
    handleSettingsChange('placeName', newPlaceName);

    // Clear any existing timeout
    if (window.placeNameTimeout) {
      clearTimeout(window.placeNameTimeout);
    }
    if (window.borderCheckTimeout) {
      clearTimeout(window.borderCheckTimeout);
    }

    // Clear results if input is empty
    if (newPlaceName.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    // Set a new timeout to geocode after 500ms of no typing
    window.placeNameTimeout = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(newPlaceName)}&limit=5`
        );
        const data = await response.json();

        if (data && data.length > 0) {
          setSearchResults(data);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: `An error in using Nominatim for ${newPlaceName}`,
          className: "bg-red-300 text-black",
          duration: 3000,
        });
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 500);
  };

  const handleResultSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    if (!isNaN(lat) && !isNaN(lon)) {
      // Update the input with the selected place name. We deliberately do
      // NOT auto-recenter the map: the user controls centering via the
      // explicit "Center" button next to the location pill, so picking a
      // suggestion only updates the stored name.
      handleSettingsChange('placeName', result.display_name);
      trackMapConfigEvents.placeSelected(result.display_name);
      rememberRecentPlace(result.display_name);

      // Hide dropdown
      setShowDropdown(false);
      setSearchResults([]);
    }
  };

  const handleSetPlace = async () => {
    if (settings.placeName.length < 2) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(settings.placeName)}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (!isNaN(lat) && !isNaN(lon)) {
          setMapCenter([lat, lon]);
          setShouldCenter(true);
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `An error in using Nominatim for ${settings.placeName}`,
        className: "bg-red-300 text-black",
        duration: 3000,
      });
    }
  };

  const generatePreviews = async () => {
    setIsGeneratingPreviews(true);
    setPreviewData(prevData =>
      Object.fromEntries(PREVIEW_LOCATIONS.map(location => [
        location,
        { ...prevData[location], status: 'generating' }
      ]))
    );

    try {
      const response = await fetch(`${API_URL}/generate_previews`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          settings: settings,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Process any remaining data in buffer
          if (buffer.trim()) {
            try {
              const data = JSON.parse(buffer.trim());
              setPreviewData(prevData => ({
                ...prevData,
                [data.location]: data
              }));
            } catch (e) {
              toast({
                title: "Error",
                description: "An error occured",
                className: "bg-red-300 text-black",
                duration: 3000,
              });
            }
          }
          break;
        }

        // Add new chunk to buffer
        buffer += decoder.decode(value, { stream: true });

        // Split by double newline and process complete messages
        const messages = buffer.split('\n\n');

        // Keep the last incomplete message in buffer
        buffer = messages.pop() || '';

        // Process complete messages
        for (const message of messages) {
          if (message.trim()) {
            try {
              const data = JSON.parse(message.trim());

              setPreviewData(prevData => ({
                ...prevData,
                [data.location]: data
              }));
            } catch (e) {
              toast({
                title: "Error",
                description: "An error occured",
                className: "bg-red-300 text-black",
                duration: 3000,
              });
            }
          }
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured",
        className: "bg-red-300 text-black",
        duration: 3000,
      });
      setPreviewData(prevData =>
        Object.fromEntries(
          PREVIEW_LOCATIONS.map(location => [
            location,
            {
              ...prevData[location],
              status: 'error',
              error: error.toString()
            }
          ])
        )
      );
    } finally {
      setIsGeneratingPreviews(false);
    }
  };

  const generateFinalPlot = async () => {
    setIsGeneratingFinal(true);
    setFinalImage(null);
    // Seed the progress UI immediately so the bar shows up before the
    // first poll lands. The backend overwrites this on its first checkpoint.
    setGenerationStatus({ state: 'running', label: 'Starting…', progress: 0 });

    const generationStartTime = Date.now();
    try {
      // Reset dimensions to S size
      const sDimensions = selectedFormat.value === 'custom'
        ? selectedFormat.dimensions // Custom format already stores dimensions
        : parseDimensions(FORMATS[selectedFormat.value].S);

      setSettings(prev => ({
        ...prev,
        figure_width: sDimensions.width,
        figure_height: sDimensions.height
      }));

      const finalDimensions = isPortrait
        ? { figure_width: sDimensions.height, figure_height: sDimensions.width }
        : { figure_width: sDimensions.width, figure_height: sDimensions.height };

      // Reuse the cached max_dpis if the new rectangle is either the exact
      // same one or "similar" (close center + similar km²) to the last one
      // we generated against — both yield the same DPI caps in practice, so
      // skipping the QGIS calibration probe is safe.
      const rectangleBoundsAreEqual = lastGeneratedSettings?.rectangleBounds &&
        JSON.stringify(lastGeneratedSettings.rectangleBounds) === JSON.stringify(rectangleBounds);
      const boundsReusable = rectangleBoundsAreEqual || rectangleBoundsAreSimilar(
        lastGeneratedSettings?.rectangleBounds,
        rectangleBounds,
        lastGeneratedSettings?.settings?.km2,
        settings?.km2,
      );
      const shouldIncludeMaxDpis = boundsReusable && maxDpis;

      setLastGeneratedSettings({
        settings: {
          ...settings,
          ...finalDimensions
        },
        selectedFormat,
        isPortrait,
        mapCenter,
        rectangleBounds,
        filteredActivities,
        allDates,
        dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
        dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
        topography,
        frame: FRAME_ENABLED ? addFrame : false,
        frameSettings,
        activitySource,
      });

      await saveToBackend();

      const requestData = {
        settings: {
          ...settings,
          ...finalDimensions
        },
        filteredActivities: activitySource === "strava" ? filteredActivities :
          (activitySource === "garmin" ? garminActivities : enabledActivities),
        selectedFormat,
        rectangleBounds,
        allDates,
        dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
        dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
        topography,
        frame: FRAME_ENABLED ? addFrame : false,
        frameSettings,
        activitySource: activitySource === "none"
          ? "none"
          : activitySource === "strava"
            ? (stravaStatus.activities ? "strava" : "none")
            : activitySource === "garmin"
              ? (garminStatus.activities ? "garmin" : "none")
              : (manualActivities.length > 0 ? "manual" : "none")
      };

      if (shouldIncludeMaxDpis) {
        requestData.maxDpis = maxDpis;
      }
      const response = await fetch(`${API_URL}/generate_final`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      const generationTimeSeconds = (Date.now() - generationStartTime) / 1000;

      if (response.status === 409) {
        // Another tab/request already holds the slot — don't show an error;
        // the polling loop will mirror the in-flight generation's progress
        // and pick up its result when done.
        return;
      }

      if (response.ok) {
        trackGenerationEvents.generateSuccess(generationTimeSeconds, requestData);
        // Cache the per-size DPI table so the next regeneration with the
        // same rectangle skips the QGIS calibration probe entirely
        // (shouldIncludeMaxDpis above honors this on the next request).
        const maxDpisHeader = response.headers.get('X-Max-Dpis');
        if (maxDpisHeader) {
          try {
            setMaxDpis(JSON.parse(maxDpisHeader));
          } catch (_e) { /* ignore malformed header */ }
        }
        const blob = await response.blob();
        setFinalImage(URL.createObjectURL(blob));
        setIsUnlocked(false);
        toast({
          title: "Map generated successfully",
          description: "Your map has been generated and is ready to view in the preview section.",
          className: "bg-black border-white text-white",
          duration: 7000,
          action: (
            <div
              className="w-full h-full absolute inset-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveMapSection('preview');
              }}
            />
          ),
        });

      } else {
        trackGenerationEvents.generateError(generationTimeSeconds, requestData);
        toast({
          title: "Error",
          description: 'Failed to generate your map. Please try again.',
          className: "bg-red-300 text-black",
          duration: 7000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while generating your map!",
        className: "bg-red-300 text-black",
        duration: 7000,
      });
    } finally {
      setIsGeneratingFinal(false);
    }
  };

  const setMaxDpisLastMap = async (maxDpis, bounds) => {
    setMaxDpis(maxDpis);
  }

  // Single source of truth for "is a generation in flight?". Combines the
  // in-flight POST (isGeneratingFinal) and the persistent server-side state
  // (generationStatus) — either alone is enough to disable the button and
  // show the progress bar.
  const isGenerationActive =
    isGeneratingFinal || generationStatus?.state === 'running';

  // On mount, ask the backend whether this account already has a generation
  // running (e.g. from another tab or a pre-reload session). If so, take
  // over with progress polling.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/user/generation-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data && data.state) {
          setGenerationStatus(data);
        }
      } catch (_e) { /* offline, no-op */ }
    })();
    return () => { cancelled = true; };
    // Run once per fresh mount; the polling effect below handles updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while a generation is running. Stops as soon as state flips out of
  // 'running' (done / error / idle). 2s cadence is a fair compromise between
  // UI responsiveness and Mongo load.
  useEffect(() => {
    if (generationStatus?.state !== 'running') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/user/generation-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) setGenerationStatus(data);
      } catch (_e) { /* transient network — keep polling */ }
    };
    const id = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(id); };
  }, [generationStatus?.state]);

  // When a generation completes (we weren't the tab that fired it, OR we
  // reloaded mid-run), the response blob never reached this tab. Recover by
  // refetching the recent maps so the latest one becomes the displayed
  // image. We only do this when finalImage is empty so a freshly-generated
  // image from THIS tab's POST isn't clobbered.
  useEffect(() => {
    if (generationStatus?.state !== 'done') return;
    if (finalImage) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/recent_maps`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        const latest = (data?.maps || [])[0];
        if (!latest) return;
        if (latest.max_dpis) setMaxDpis(latest.max_dpis);
        // The /recent_maps payload exposes a watermark URL the dashboard
        // can display directly without a second blob fetch.
        if (latest.imageUrl) setFinalImage(latest.imageUrl);
      } catch (_e) { /* swallow — next generation will refresh */ }
    })();
  }, [generationStatus?.state, finalImage]);

  const handleSizeSelect = async (imageId, size, dimensions, needsRegeneration) => {
    setIsUnlocking(true);
    try {
      if (!imageId) {
        toast({
          title: "Error",
          description: "No image to unlock",
          variant: "destructive",
        });
        return;
      }
      let height = dimensions.height;
      let width = dimensions.width;
      if (size === "custom") {
        height = dimensions.originalHeight;
        width = dimensions.originalWidth;
      }
      trackPurchaseEvents.checkoutInitiated(size, width, width);
      // Create checkout session first
      const checkoutResponse = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageId: imageId,
          size: size,
          height: height,
          width: width,
          needsRegeneration,
          voucher: voucher
        }),
      });

      if (!checkoutResponse.ok) {
        trackPurchaseEvents.checkoutFailed();
        throw new Error('Failed to create checkout session');
      }
      localStorage.setItem('currentImageId', imageId);
      const { checkoutUrl } = await checkoutResponse.json();

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;

    } catch (error) {
      trackPurchaseEvents.checkoutFailed();
      toast({
        title: "Error",
        description: "An error occured",
        className: "bg-red-300 text-black",
        duration: 3000,
      });
      setIsUnlocking(false);
    }
  };

  const haveSettingsChanged = () => {
    if (!lastGeneratedSettings) return true;

    // Deep comparison of relevant settings
    const relevantCurrentSettings = {
      settings,
      selectedFormat,
      isPortrait,//: lastGeneratedSettings.isPortrait, // Use the portrait setting from last generation
      mapCenter,
      rectangleBounds,
      filteredActivities,
      allDates,
      dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
      dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
      topography,
      frame: FRAME_ENABLED ? addFrame : false,
      frameSettings,
      activitySource
    };
    relevantCurrentSettings.settings.figure_width = lastGeneratedSettings.settings.figure_width;
    relevantCurrentSettings.settings.figure_height = lastGeneratedSettings.settings.figure_height;

    const relevantLastSettings = {
      ...lastGeneratedSettings
    };
    return JSON.stringify(relevantCurrentSettings) !== JSON.stringify(relevantLastSettings);
  };

  const handleResetToPlace = () => {
    handlePlaceNameChange({ target: { value: settings.placeName } });
  }

  const FormatCard = ({ format, isSelected, onSelect }) => {
    return (
      <Card
        className={`cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
        onClick={() => onSelect(format.value)}
      >
        <CardContent className="flex mt-2 items-center justify-center h-full p-0">
          <span className="text-sm font-medium text-center">{format.label}</span>
        </CardContent>
      </Card>
    );
  };

  const handleDeleteManualActivity = async (activityId) => {
    try {
      const response = await fetch(`${API_URL}/delete_gpx/${activityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        trackActivityEvents.gpxDelete();
        setManualActivities(prev => prev.filter(activity => activity._id !== activityId));
        setEnabledActivities(prev => {
          const newState = { ...prev };
          delete newState[activityId];
          return newState;
        });
      }
    } catch (error) {
      trackActivityEvents.gpxDeleteError();
      toast({
        title: "Error",
        description: "An error occured while deleting a GPX",
        className: "bg-red-300 text-black",
        duration: 3000,
      });
    }
  };

  const SettingSection = ({ title, children }) => (
    <div className="flex flex-col">
      <span className="text-sm text-gray-500 mb-1">{title}</span>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );

  const StatusIcon = ({ active }) => (
    active ?
      <Check className="h-4 w-4 text-green-500 inline-block" /> :
      <X className="h-4 w-4 text-red-500 inline-block" />
  );

  const cn = isPortrait
    ? "grid grid-cols-1 gap-4 mb-6"
    : "grid grid-cols-2 md:grid-cols-4 gap-4 mb-6";

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 container mx-auto px-2 lg:px-4 lg:py-6 h-full">
        <Tabs value={selectedTab} onValueChange={handleTabChange} className="flex flex-col h-full">
          {/* Tab navigation has moved to the top header (App.js) — the
              TabsList here is intentionally omitted. Create is the default
              view; header buttons switch to history / admin. */}

          <TabsContent value="create" className="flex-1 h-full">
            <div className="flex flex-col h-full">
              <div className="mb-4">
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => {
                      const currentIndex = mapSections.findIndex(section => section.id === activeMapSection);
                      if (currentIndex > 0) {
                        handleSectionChange(mapSections[currentIndex - 1].id);
                      }
                    }}
                    disabled={activeMapSection === mapSections[0].id}
                    // className="p-2 bg-blue-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    className="bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-2xl"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex-1 border-0 bg-gray-100 rounded-2xl p-2">
                    <div className="flex flex-wrap gap-0 lg:gap-2 justify-center items-center">
                      {mapSections.map((section, index) => (
                        <button
                          key={section.id}
                          onClick={() => handleSectionChange(section.id)}
                          className={`flex items-center min-w-fit px-1 lg:px-3 py-0.5 lg:py-1.5 rounded-2xl transition-colors
                 ${activeMapSection === section.id ? 'bg-black text-white' : 'hover:bg-gray-200'}`}
                        >
                          <div className="flex items-center gap-1.5 lg:gap-2.5">
                            <span className="flex items-center justify-center w-4 h-4 lg:w-6 lg:h-6 text-md lg:text-lg rounded-full bg-white/20">
                              {index + 1}
                            </span>
                            <span>{section.icon}</span>
                            <span className="text-sm hidden sm:inline">{section.label}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={() => {
                      const currentIndex = mapSections.findIndex(section => section.id === activeMapSection);
                      if (currentIndex < mapSections.length - 1) {
                        handleSectionChange(mapSections[currentIndex + 1].id);
                      }
                    }}
                    disabled={activeMapSection === mapSections[mapSections.length - 1].id}
                    // className="p-2 bg-blue-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    className="bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-2xl"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* <div className="flex-1 bg-orange-100/80 rounded-xl"> */}
              <div
                className="flex rounded-2xl shadow-xl"
                style={{
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.02), rgba(0,0,60,0.15))'
                }}
              >
                {activeMapSection === 'style' && (
                  <div className={`h-full p-4 lg:p-8 w-full grid grid-cols-1 ${customizeOpen ? '' : 'lg:grid-cols-4'} gap-4 lg:gap-6`}>
                    <div className={customizeOpen ? 'min-w-0 w-full max-w-4xl mx-auto' : 'lg:col-span-3 min-w-0'}>
                      {customizeOpen ? (
                        <div className="flex flex-col">
                          <div className="flex items-center mb-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (expandedCategory) {
                                  setExpandedCategory(null);
                                } else {
                                  setCustomizeOpen(false);
                                }
                              }}
                              className="flex items-center gap-1 text-gray-700 hover:bg-gray-200 rounded-xl"
                            >
                              <ArrowLeft className="h-4 w-4" />
                              Back
                            </Button>
                          </div>
                          <div className="md:max-h-[calc(3*10rem)] lg:max-h-[calc(4*10rem)] md:overflow-y-auto pr-1">
                            <ColorSettingsPopup
                              settings={settings}
                              onColorSettingChange={handleColorSettingChange}
                              onActivityTypeSettingChange={handleActivityTypeSettingChange}
                              activitySource={activitySource}
                              triggerButton={false}
                              syncOceansWater={syncWaterOceans}
                              onSyncToggle={handleSyncWaterOceans}
                              selectedCategory={expandedCategory}
                              onSelectedCategoryChange={setExpandedCategory}
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          {styleSource === 'classic' ? (
                            <StaticImageSlideshow
                              onSettingsChange={(newSettings) => {
                                setSettings(prevSettings => ({
                                  ...prevSettings,
                                  ...newSettings
                                }));
                              }}
                            >
                              <Button
                                size="sm"
                                onClick={() => setCustomizeOpen(true)}
                                className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
                              >
                                <SlidersHorizontal className="h-4 w-4 mr-2" />
                                Customize
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setStyleSource('shared')}
                                className="text-xs lg:text-md"
                              >
                                Styles shared by users
                              </Button>
                            </StaticImageSlideshow>
                          ) : (
                            <SharedStylesGrid
                              onSettingsChange={(newSettings) => {
                                setSettings(prevSettings => ({
                                  ...prevSettings,
                                  ...newSettings
                                }));
                              }}
                            >
                              <Button
                                size="sm"
                                onClick={() => setCustomizeOpen(true)}
                                className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
                              >
                                <SlidersHorizontal className="h-4 w-4 mr-2" />
                                Customize
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setStyleSource('classic')}
                                className="text-xs lg:text-md"
                              >
                                Classic styles
                              </Button>
                            </SharedStylesGrid>
                          )}
                          <p className='mt-4'>
                            {styleSource === 'classic'
                              ? 'Select a predefined style or build your a custom style!'
                              : 'Browse styles shared by other users.'}
                          </p>
                        </div>
                      )}
                    </div>
                    {!customizeOpen && (
                      <div className="lg:col-span-1 flex flex-col items-center justify-center">
                        <p className="text-sm font-semibold text-gray-700">Current settings</p>
                        <ColorLegend
                          settings={settings}
                          direction="vertical"
                          onOpenCategory={(category) => {
                            setExpandedCategory(category);
                            setCustomizeOpen(true);
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
                {activeMapSection === 'map' && (
                  <div className="flex flex-col h-full p-4 lg:p-8 w-full">
                    {/* Grid template varies by state so the right column
                        doesn't leave empty space:
                          - collapsed: single column, map full width
                          - expanded + 'none': [1fr auto] so the bar hugs
                            the map's right edge with no slack
                          - expanded + source: 5-col with 3:2 split */}
                    <div className={`grid grid-cols-1 gap-4 lg:gap-8 w-full ${!activitiesPanelOpen
                      ? 'lg:grid-cols-1'
                      : activitySource === 'none'
                        ? 'lg:grid-cols-[1fr_auto]'
                        : 'lg:grid-cols-5'}`}>
                      {/* Left: top controls + map (spans only above the map) */}
                      <div className={`w-full space-y-3 relative ${activitiesPanelOpen && activitySource !== 'none' ? 'lg:col-span-3' : ''}`}>

                        {settings.km2 > 15000 && (
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="text-orange-500 flex-shrink-0" size={16} />
                            <span className="text-xs lg:text-sm text-gray-600">
                              Buildings & streets are disabled for areas bigger than 8000km²
                            </span>
                          </div>
                        )}
                        {settings.km2 > 4000 && settings.km2 <= 15000 && (
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="text-orange-500 flex-shrink-0" size={16} />
                            <span className="text-xs lg:text-sm text-gray-600">
                              Streets can only be plotted in reduced mode for areas bigger than 4000km²
                            </span>
                          </div>
                        )}

                        {settingsLoaded && mapCenter[0] !== null && mapCenter[1] !== null ? (
                          <MapComponent
                            mapCenter={mapCenter}
                            zoom={zoom}
                            onZoomChange={setZoom}
                            restoreViewNonce={restoreViewNonce}
                            rectangleBounds={rectangleBounds}
                            setRectangleBounds={setRectangleBounds}
                            km2={settings.km2}
                            selectedFormat={selectedFormat}
                            placeName={settings.placeName}
                            onMapCenterChange={handleMapCenterChange}
                            shouldCenter={shouldCenter}
                            setShouldCenter={setShouldCenter}
                            shouldZoom={shouldZoom}
                            activitySettings={settings.activities}
                            onActivityLineWidthChange={(v) => handleColorSettingChange('activities', 'lineWidth', v)}
                            showActivityPolylines={settings.activities?.active !== false}
                            activitySources={activitySource === 'none' ? [] : [activitySource]}
                            activityDateFrom={fromDate}
                            activityDateTo={toDate}
                            activityAllDates={allDates}
                            includeCommutes={activitySource !== 'manual' && commutes}
                            mapStyle={mapStyle}
                            onMapStyleChange={setMapStyle}
                            hiddenActivityIds={hiddenActivityIds}
                            onActivitiesChange={setActivitiesInView}
                            compact={true}
                            onKm2Change={(v) => handleSettingsChange('km2', v)}
                            onFormatSelect={handleFormatSelect}
                            isPortrait={isPortrait}
                            onTogglePortrait={togglePortrait}
                            onPlaceNameChange={handlePlaceNameChange}
                            searchResults={searchResults}
                            onSelectResult={handleResultSelect}
                            recentPlaces={recentPlaces}
                            onSelectRecent={(name) => {
                              // Recent picks update the stored name only;
                              // user explicitly hits the Center button when
                              // they actually want to move the map.
                              handleSettingsChange('placeName', name);
                              rememberRecentPlace(name);
                            }}
                            onClearPlace={() => {
                              handleSettingsChange('placeName', '');
                              setSearchResults([]);
                            }}
                            onCenterPlace={() => setShouldCenter(true)}
                          />
                        ) : (
                          <div className="h-[70vh] lg:aspect-[4/3] flex items-center justify-center bg-gray-50 rounded">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                          </div>
                        )}

                        {/* Floating "Plot activities" affordance: sits
                            vertically centered on the map's right edge while
                            the panel is collapsed, so there's always a clear
                            way to reveal the sidebar without leaving the map
                            area. Uses the Strava + Garmin logos as the call
                            to action. */}
                        {!activitiesPanelOpen && (
                          <button
                            type="button"
                            onClick={() => setActivitiesPanelOpen(true)}
                            title="Show activity filters & sync"
                            className="flex absolute right-1 lg:right-2 top-1/2 -translate-y-1/2 z-[900] items-center gap-1 lg:gap-2 bg-blue-600 hover:bg-blue-700 text-white border border-blue-700 rounded-l-xl shadow-lg pl-2 pr-1 py-2 lg:pl-3 lg:pr-2 lg:py-3"
                          >
                            <span className="text-[10px] lg:text-xs font-semibold whitespace-nowrap leading-tight">
                              Plot<br />activities
                            </span>
                            <div className="flex flex-col gap-0.5 lg:gap-1">
                              <img
                                src={StravaPoweredLogo2}
                                alt="Strava"
                                className="h-3.5 lg:h-5 w-auto object-contain bg-white rounded p-0.5"
                              />
                              <img
                                src={GarminLogo}
                                alt="Garmin"
                                className="h-3.5 lg:h-5 w-auto object-contain bg-white rounded p-0.5"
                              />
                            </div>
                          </button>
                        )}
                      </div>

                      {/* Right: activities panel. Collapsed by default so the
                          map takes the full width; the floating button on the
                          map's right edge reveals this panel. Location / size
                          / format controls live inside the map itself. */}
                      {activitiesPanelOpen && (
                        <div className={`w-full flex flex-col gap-2 lg:gap-3 lg:h-full order-first lg:order-none ${activitySource === 'none' ? '' : 'lg:col-span-2'}`}>
                          {/* Hide bar: top-left compact on desktop, full-width
                              horizontal bar at the BOTTOM of the panel on
                              mobile (which sits right above the map, since
                              the panel is order-first on mobile). */}
                          <div className="flex-shrink-0 order-last lg:order-first lg:flex lg:justify-start">
                            <button
                              type="button"
                              onClick={() => setActivitiesPanelOpen(false)}
                              title="Hide panel to enlarge the map"
                              className="w-full lg:w-[74px] rounded-xl border border-gray-300 bg-gray-300 text-gray-700 hover:bg-gray-200 px-3 py-2 flex flex-row lg:flex-col items-center justify-center gap-1 lg:gap-0.5 leading-tight"
                            >
                              <span className="text-xs font-semibold">Hide</span>
                              <ArrowRight className="h-3.5 w-3.5 rotate-90 lg:rotate-0" />
                            </button>
                          </div>
                          {false && (
                            <div className="border rounded-xl bg-white p-4 space-y-4">
                              <h3 className="text-sm font-semibold">Location</h3>

                              {recentPlaces.length > 0 && (
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-500">Recent</div>
                                  <div className="flex flex-wrap gap-2">
                                    {recentPlaces.map(name => (
                                      <button
                                        key={name}
                                        type="button"
                                        onClick={() => {
                                          handleSettingsChange('placeName', name);
                                          setShouldCenter(true);
                                          rememberRecentPlace(name);
                                        }}
                                        className="px-3 py-1.5 text-xs rounded-full border border-gray-300 bg-white hover:bg-gray-100 truncate max-w-full"
                                        title={name}
                                      >
                                        {name.split(',')[0]}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-600 pointer-events-none" />
                                <Input
                                  id="place-name"
                                  type="text"
                                  value={settings.placeName}
                                  onChange={handlePlaceNameChange}
                                  onFocus={() => setShowDropdown(searchResults.length > 0)}
                                  onBlur={() => { setTimeout(() => setShowDropdown(false), 150); }}
                                  className={`w-full h-11 pl-10 border-2 focus:border-blue-500 bg-gray-50 focus:bg-white rounded-xl shadow-sm text-gray-900 placeholder-gray-500 ${settings.placeName ? 'pr-10' : 'pr-3'}`}
                                  placeholder="Search a place, or drag the map"
                                  autoFocus
                                />
                                {settings.placeName && (
                                  <button
                                    type="button"
                                    aria-label="Clear location"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      handleSettingsChange('placeName', '');
                                      setSearchResults([]);
                                      setShowDropdown(false);
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-200"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                                {showDropdown && searchResults.length > 0 && (
                                  <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                    {searchResults.map((result, index) => (
                                      <div
                                        key={index}
                                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm truncate"
                                        title={result.display_name}
                                        onClick={() => handleResultSelect(result)}
                                      >
                                        {result.display_name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {false && (
                            <div className="border rounded-xl bg-white p-4 space-y-4">
                              <h3 className="text-sm font-semibold">Format</h3>
                              <div className="grid grid-cols-2 gap-2">
                                {Object.keys(FORMATS).map((format) => {
                                  const [w, h] = FORMATS[format].S.split('x').map(d => parseFloat(d.replace(',', '.')));
                                  const display = isPortrait ? `${w}×${h}` : `${h}×${w}`;
                                  const isActive = selectedFormat?.value === format;
                                  return (
                                    <button
                                      key={format}
                                      type="button"
                                      onClick={() => {
                                        const dims = {
                                          width: w,
                                          height: h,
                                          originalWidth: isPortrait ? h : w,
                                          originalHeight: isPortrait ? w : h,
                                        };
                                        handleFormatSelect({
                                          value: format,
                                          size: 'S',
                                          label: ` ${format}`,
                                          dimensions: dims,
                                          aspectRatio: dims.width / dims.height,
                                        });
                                      }}
                                      className={`p-3 border rounded-xl text-center transition-colors ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                      <div className="font-medium">{format}</div>
                                      <div className="text-xs text-gray-500">{display} cm</div>
                                    </button>
                                  );
                                })}
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Seed sensible defaults and switch the inputs
                                    // visible. Any real ratio is committed via Apply.
                                    setCustomRatioW(customRatioW || '4');
                                    setCustomRatioH(customRatioH || '3');
                                    const w = parseFloat(customRatioW || '4');
                                    const h = parseFloat(customRatioH || '3');
                                    const longest = 30;
                                    const scale = longest / Math.max(w, h);
                                    const sw = w * scale;
                                    const sh = h * scale;
                                    handleFormatSelect({
                                      value: 'custom',
                                      size: 'S',
                                      label: ` ${w}:${h}`,
                                      dimensions: {
                                        width: sw,
                                        height: sh,
                                        originalWidth: isPortrait ? sh : sw,
                                        originalHeight: isPortrait ? sw : sh,
                                      },
                                      aspectRatio: isPortrait ? sh / sw : sw / sh,
                                    });
                                  }}
                                  className={`p-3 border rounded-xl text-center transition-colors ${selectedFormat?.value === 'custom' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className="font-medium">Custom</div>
                                  <div className="text-xs text-gray-500">
                                    {selectedFormat?.value === 'custom' ? selectedFormat.label : 'W : H'}
                                  </div>
                                </button>
                              </div>

                              {selectedFormat?.value === 'custom' && (
                                <div className="pt-3 border-t space-y-2 flex flex-col items-center">
                                  <div className="text-xs text-gray-600">Custom aspect ratio</div>
                                  <div className="flex items-center gap-2 justify-center">
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder="W"
                                      value={customRatioW}
                                      onChange={(e) => setCustomRatioW(e.target.value)}
                                      className="w-16 text-center border bg-white rounded-xl"
                                    />
                                    <span className="text-lg font-medium text-gray-500">:</span>
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder="H"
                                      value={customRatioH}
                                      onChange={(e) => setCustomRatioH(e.target.value)}
                                      className="w-16 text-center border bg-white rounded-xl"
                                    />
                                    <Button
                                      type="button"
                                      onClick={() => {
                                        const w = parseFloat(customRatioW);
                                        const h = parseFloat(customRatioH);
                                        if (!(w > 0 && h > 0)) return;
                                        const longest = 30;
                                        const scale = longest / Math.max(w, h);
                                        const sw = w * scale;
                                        const sh = h * scale;
                                        handleFormatSelect({
                                          value: 'custom',
                                          size: 'S',
                                          label: ` ${w}:${h}`,
                                          dimensions: {
                                            width: sw,
                                            height: sh,
                                            originalWidth: isPortrait ? sh : sw,
                                            originalHeight: isPortrait ? sw : sh,
                                          },
                                          aspectRatio: isPortrait ? sh / sw : sw / sh,
                                        });
                                      }}
                                      disabled={!(parseFloat(customRatioW) > 0 && parseFloat(customRatioH) > 0)}
                                      className="rounded-2xl border-0 bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
                                    >
                                      Apply
                                    </Button>
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center gap-3 pt-3 border-t justify-center">
                                <Checkbox
                                  id="portrait-mode"
                                  checked={isPortrait}
                                  onCheckedChange={togglePortrait}
                                  className="h-6 w-6 border-black rounded-xl"
                                />
                                <Label htmlFor="portrait-mode" className="text-sm font-medium cursor-pointer">
                                  Portrait orientation
                                </Label>
                              </div>
                            </div>
                          )}

                          {false && (
                            <div className="border rounded-xl bg-white p-6 space-y-6">
                              <h3 className="text-base font-semibold text-center">Area size</h3>
                              <div className="flex items-baseline gap-2 justify-center">
                                <Input
                                  id="area-size"
                                  type="number"
                                  value={settings.km2}
                                  onChange={handlekm2change}
                                  min={1}
                                  className="w-[140px] text-center text-lg border-2 focus:border-blue-500 bg-gray-50 focus:bg-white rounded-xl"
                                />
                                <span className="text-sm text-gray-600">km²</span>
                              </div>
                              <div className="pt-2 pb-2">
                                <Slider
                                  value={[Math.min(Number(settings.km2) || 1, 20000)]}
                                  min={1}
                                  max={20000}
                                  step={1}
                                  onValueChange={(v) => handleSettingsChange('km2', v[0])}
                                />
                                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                                  <span>1</span>
                                  <span>5 000</span>
                                  <span>10 000</span>
                                  <span>20 000</span>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="text-xs text-gray-500">Quick picks</div>
                                <div className="flex flex-wrap gap-2">
                                  {[10, 50, 100, 500, 1000, 5000, 10000].map(preset => (
                                    <button
                                      key={preset}
                                      type="button"
                                      onClick={() => handleSettingsChange('km2', preset)}
                                      className={`px-3 py-1.5 text-sm rounded-full border ${Number(settings.km2) === preset
                                        ? 'bg-gray-900 text-white border-gray-900'
                                        : 'bg-white border-gray-300 hover:bg-gray-100'
                                        }`}
                                    >
                                      {preset} km²
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {activitiesPanelOpen && (
                            <div className="w-full flex flex-col lg:flex-row gap-2 lg:gap-3 items-stretch flex-1 min-h-0">
                              {/* Source switcher. Vertical on desktop (sits
                                  next to the map with vertical-rl text),
                                  horizontal on mobile (row of icon+label
                                  buttons stacked above the content). */}
                              <div className="inline-flex flex-row lg:flex-col items-stretch gap-1 border border-gray-200 bg-gray-50 rounded-xl p-1 flex-shrink-0 lg:h-full">
                                <button
                                  type="button"
                                  title="Manual GPX upload"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex flex-row lg:flex-col items-center justify-center gap-1 text-xs font-semibold ${activitySource === 'manual' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-700 hover:bg-gray-200'}`}
                                  onClick={() => handleActivitySourceChange('manual')}
                                >
                                  <Upload className="h-4 w-4" />
                                  <span className="whitespace-nowrap lg:[writing-mode:vertical-lr] lg:rotate-180">GPX</span>
                                </button>
                                <button
                                  type="button"
                                  title="Strava sync"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex items-center justify-center overflow-hidden ${activitySource === 'strava' ? 'bg-blue-600' : 'bg-transparent hover:bg-gray-200'}`}
                                  onClick={() => handleActivitySourceChange('strava')}
                                >
                                  <img src={StravaPoweredLogo2} alt="Strava" className="h-6 lg:h-14 w-auto object-contain lg:-rotate-90" />
                                </button>
                                <button
                                  type="button"
                                  title="Garmin sync"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex items-center justify-center overflow-hidden ${activitySource === 'garmin' ? 'bg-blue-600' : 'bg-transparent hover:bg-gray-200'}`}
                                  onClick={() => handleActivitySourceChange('garmin')}
                                >
                                  <img src={GarminLogo} alt="Garmin" className="h-6 lg:h-14 w-auto object-contain lg:-rotate-90" />
                                </button>
                                <button
                                  type="button"
                                  title="Generate without activities"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex items-center justify-center text-[10px] lg:text-xs font-semibold ${activitySource === 'none' ? 'bg-blue-600 text-white' : 'bg-transparent text-gray-700 hover:bg-gray-200'}`}
                                  onClick={() => handleActivitySourceChange('none')}
                                >
                                  <span className="whitespace-nowrap lg:[writing-mode:vertical-lr] lg:rotate-180">No activities</span>
                                </button>
                              </div>

                              <div className="flex-1 min-w-0 w-full space-y-2 lg:space-y-4 overflow-y-auto">
                                {activitySource === 'strava' && (
                                  <div className="flex flex-col space-y-4 items-center justify-center">
                                    <StravaComponent
                                      stravaStatus={stravaStatus}
                                      onStatusUpdate={setStravaStatus}
                                      filteredActivities={filteredActivities}
                                      onSetFilteredActivities={handleActivitySelectionChange}
                                      allDates={allDates}
                                      fromDate={fromDate}
                                      toDate={toDate}
                                      onAllDatesChange={setAllDates}
                                      onFromDateChange={setFromDate}
                                      onToDateChange={setToDate}
                                    />
                                  </div>
                                )}

                                {activitySource === 'garmin' && (
                                  <div className="flex space-y-4 justify-center">
                                    <GarminComponent
                                      garminStatus={garminStatus}
                                      onStatusUpdate={setGarminStatus}
                                      filteredActivities={garminActivities}
                                      onSetFilteredActivities={handleActivitySelectionChange}
                                      allDates={allDates}
                                      fromDate={fromDate}
                                      toDate={toDate}
                                      onAllDatesChange={setAllDates}
                                      onFromDateChange={setFromDate}
                                      onToDateChange={setToDate}
                                    />
                                  </div>
                                )}
                                {activitySource !== 'none' && (
                                  <div className="mt-6">
                                    <h3 className="text-lg font-semibold mb-2">Activities in view</h3>
                                    <ActivityFilterPanel
                                      activities={activitiesInView}
                                      activitySettings={settings.activities}
                                      hiddenIds={hiddenActivityIds}
                                      onToggle={(id) =>
                                        setHiddenActivityIds(prev =>
                                          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                                        )
                                      }
                                      onToggleType={(type, hideAll) => {
                                        const typeIds = activitiesInView
                                          .filter(a => a.type === type)
                                          .map(a => a.id);
                                        setHiddenActivityIds(prev => {
                                          if (hideAll) {
                                            return Array.from(new Set([...prev, ...typeIds]));
                                          }
                                          return prev.filter(id => !typeIds.includes(id));
                                        });
                                      }}
                                      commutes={activitySource === 'manual' ? undefined : commutes}
                                      onCommutesChange={activitySource === 'manual' ? undefined : setCommutes}
                                      allDates={allDates}
                                      onAllDatesChange={setAllDates}
                                      fromDate={fromDate}
                                      toDate={toDate}
                                      onFromDateChange={setFromDate}
                                      onToDateChange={setToDate}
                                      onDeleteActivity={activitySource === 'manual' ? handleDeleteManualActivity : undefined}
                                      dateRightSlot={activitySource === 'manual' ? (
                                        <GPXUploadHandler compact onUploadComplete={fetchManualActivities} />
                                      ) : null}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <FormatSelectorPopup
                      isOpen={isFormatSelectorOpen}
                      onClose={() => setIsFormatSelectorOpen(false)}
                      onFormatSelect={handleFormatSelect}
                      selectedFormat={selectedFormat}
                      isPortrait={isPortrait}
                    />
                  </div>
                )}
                {activeMapSection === 'frame' && (
                  <div className="flex flex-col h-full p-4 lg:p-8 items-center w-full">
                    {/* <h2 className="text-3xl font-bold text-center mb-8">Add a Frame</h2> */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center">
                      {/* Frame settings */}
                      <div className="space-y-2 lg:space-y-4">
                        <div className="flex items-center justify-center lg:mb-10">
                          {/* <Button
                            onClick={toggleFrame}
                            variant="black"
                            className="px-4 py-2 mb-4"
                          >
                            {addFrame ? 'Remove custom frame' : 'Add custom frame'}
                          </Button> */}
                          <div className='flex gap-2 items-center'>
                            <Checkbox
                              id="customFrame"
                              checked={addFrame}
                              onCheckedChange={(checked) => toggleFrame()}
                              className="h-6 w-6 border-black rounded-xl"
                            />
                            <p className='font-bold text-sm lg:text-lg'>
                              Add a Frame
                            </p>
                          </div>
                        </div>
                        <div className={`${!addFrame ? 'opacity-80 pointer-events-none' : ''}`}>
                          <IntegratedFrameSettings
                            frameSettings={frameSettings}
                            onSettingsChange={handleFrameSettingsChange}
                            disabled={!addFrame}
                          />
                        </div>
                      </div>
                      {/* Frame preview */}
                      <div className="flex justify-center items-center relative">
                        {addFrame && frameImage ? (
                          <div className="shadow-2xl overflow-hidden relative">
                            <img
                              src={frameImage}
                              alt="Frame preview"
                              className="max-w-full max-h-[600px]"
                            />
                            {isLoadingFrame && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                                <Loader2 className="w-8 h-8 animate-spin text-white" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="shadow-2xl overflow-hidden">
                            <img
                              src={borderExample}
                              alt="Border example"
                              className="max-w-full max-h-[600px] opacity-40"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {activeMapSection === 'preview' && (
                  <div className="flex flex-col h-full p-4 lg:p-8 items-center w-full">
                    {/* <h2 className="text-3xl font-bold text-center mb-6">Preview and Checkout</h2> */}
                    <div className="flex flex-col items-center space-y-4 w-full">
                      <div className="flex flex-col items-center w-full gap-3">
                        <Button
                          onClick={generateFinalPlot}
                          disabled={isGenerationActive || !haveSettingsChanged()}
                          className={`
                              relative px-4 py-2 rounded-xl w-full max-w-xs
                              flex items-center justify-center
                              transition-all duration-3000
                              border-0
                              ${isGenerationActive || !haveSettingsChanged()
                              ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed shadow-gray-300"
                              : `
                                  bg-gradient-to-r from-black/90 via-black/100 to-black/60
                                  bg-[length:200%_auto]
                                  animate-shine
                                  text-white font-medium
                                  hover:scale-105
                                `}
                            `}
                        >
                          {isGenerationActive ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Generating...
                            </>
                          ) : !haveSettingsChanged() ? (
                            <>
                              <ImageIcon className="mr-2 h-4 w-4" />
                              No changed settings
                            </>
                          ) : (
                            <>
                              <ImageIcon className="mr-2 h-4 w-4" />
                              Generate map
                            </>
                          )}
                        </Button>
                        <GenerationProgress status={generationStatus} />
                      </div>
                      <MobileMapLayout
                        finalImage={finalImage}
                        lastGeneratedSettings={lastGeneratedSettings}
                        isPortrait={isPortrait}
                        setEnlargedImage={setEnlargedImage}
                        selectedFormat={selectedFormat}
                        handleSizeSelect={handleSizeSelect}
                        isUnlocking={isUnlocking}
                        setVoucher={setVoucher}
                        setSelectedTab={handleTabChange}
                        setMaxDpisLastMap={setMaxDpisLastMap}
                      />
                    </div>
                    {enlargedImage && (
                      <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
                        onClick={() => setEnlargedImage(null)}
                      >
                        <div
                          className="bg-white p-4 rounded-2xl max-w-6xl mx-4 relative "
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <h3 className="text-lg font-semibold">Preview of your map</h3>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEnlargedImage(null)}
                              className="text-gray-500 hover:text-gray"
                            >
                              <X className="h-6 w-6" />
                            </Button>
                          </div>
                          <img
                            src={enlargedImage.imageUrl}
                            alt={`Enlarged preview for ${enlargedImage.name}`}
                            className="w-full max-h-[80vh] object-scale-down shadow-2xl shadow-black/50"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* <TabsContent value="history" className="flex-1 h-[calc(100vh-16rem)] overflow-y-auto"> */}
          <TabsContent value="history" className="flex-1 h-full overflow-y-auto">
            <div className="mb-4">
              <Button
                onClick={() => handleTabChange('create')}
                size="sm"
                className="rounded-2xl bg-gray-900 border-0 text-white hover:bg-gray-600"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Create Map
              </Button>
            </div>
            <ImageSlideshow
              // Pass the lifted state
              images={images}
              setImages={setImages}
              imageUrls={imageUrls}
              setImageUrls={setImageUrls}
              onSettingsChange={(newSettings) => {
                setSettings(prevSettings => ({
                  ...prevSettings,
                  ...newSettings
                }));
              }}
            />
          </TabsContent>

          <TabsContent value="admin" className="flex-1 h-[calc(100vh-16rem)]">
            <div className="p-6 h-full flex flex-col min-h-0">
              <div className="mb-4 flex-shrink-0">
                <Button
                  onClick={() => handleTabChange('create')}
                  size="sm"
                  className="rounded-2xl bg-gray-900 border-0 text-white hover:bg-gray-600"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Create Map
                </Button>
              </div>
              <div className="flex-1 min-h-0">
                <AdminPanel />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
